<template>
  <UserCommonLayout :main-class-name="'pt0'" :footer-class-name="'line'">
    <template v-if="!isLoading" #content>
      <LazyProSkillManagerCommonBreadCrumb :path-name="assessmentDetailInfo.title" />
      <LazyProSkillManagerAssessmentDetailBanner
:assessment-info="assessmentDetailInfo"
                                                 :assessment-id="assessmentId" />
      <LazyProSkillManagerAssessmentDetailTab :tab-type="detailTab" />
      <LazyProSkillManagerAssessmentDetail
          :key="detailTab"
          :tab-type="detailTab"
          :assessment-summary="assessmentSummary"
          :track-summaries="trackSummary"
          :skill-summaries="skillSummary"
          :candidate-summary="candidateSummary"
          :candidate-integrity-page-data="candidateIntegrityPageData"
          :assessment-id="assessmentId"
      />
    </template>
  </UserCommonLayout>
</template>

<script setup lang="ts">
import {
  LazyProSkillManagerAssessmentDetail,
  LazyProSkillManagerAssessmentDetailBanner,
  LazyProSkillManagerAssessmentDetailTab,
  LazyProSkillManagerCommonBreadCrumb
} from "#components";
import { useI18n } from "vue-i18n";
import { computed, onMounted, provide, ref, watch } from "vue";
import { useRoute, useRouter } from "#imports";
import { useLoadingStore } from "~/store/loading/loadingStore";
import { useDialogStore } from "~~/application/store/dialog/dialogStore";
import { useGenericDialog } from "~/composables/useGenericDialog";
import {
  ProSkillManagerAssessmentDetailInfoModel
} from "~~/package/mapper/proSkillManager/model/assessment/ProSkillManagerAssessmentDetailInfoModel";
import {
  ProSkillManagerAssessmentCandidateSummaryModel
} from "~~/package/mapper/proSkillManager/model/candidate/ProSkillManagerAssessmentCandidateSummaryModel";
import { AssessmentSummaryModel } from "~~/package/mapper/report/model/assessment/AssessmentSummaryModel";
import type { TrackSummaryModel } from "~~/package/mapper/report/model/assessment/TrackSummaryModel";
import type { SkillSummaryModel } from "~~/package/mapper/report/model/assessment/SkillSummaryModel";
import { useProSkillManagerService } from "~~/application/composables/service/useProSkillManagerService";
import { useReportService } from "~/composables/service/useReportService";
import { ReportDomainType } from "~~/package/mapper/report/model/ReportDomainType";
import { ReportTabType } from "~~/package/mapper/report/model/ReportTabType";
import DialogType from "~~/application/components/Common/Dialog/model/DialogType";
import {
  ProSkillManagerCandidateModel
} from "~~/package/mapper/proSkillManager/model/candidate/ProSkillManagerCandidateModel";
import { PageModel } from "~~/package/mapper/common/model/PageModel";
import {
  ProSkillManagerCandidateStatusType
} from "~~/package/mapper/proSkillManager/model/candidate/ProSkillManagerCandidateStatusType";
import {PRO_SKILL_MONITOR_CANDIDATE_REFRESH, PRO_SKILL_MONITOR_SORT_REFRESH} from '~/keys/ProSkillMonitor';
import {useUserStore} from '~/store/user/userStore';

const { t } = useI18n();
const { $i18n } = useNuxtApp();
// ASMT-REPORT-14: 현재 UI 언어를 리포트 요약 조회 LANG으로 전달(서버가 선택 언어 기준 fallback)
const lang = computed(() => $i18n.locale.value.toUpperCase());
const { guard } = usePermissionGuard();
const route = useRoute();
const router = useRouter();

const loadingStore = useLoadingStore();
const dialogStore = useDialogStore();
const userStore = useUserStore();
const { openGenericAlert } = useGenericDialog();

const proSkillManagerService = useProSkillManagerService();
const reportService = useReportService();

const assessmentId = computed(() => parseInt(route.params.assessmentId as string));
const detailTab = ref(ReportTabType.ASSESSMENT);
const isLoading = ref(true);

const assessmentDetailInfo = ref(new ProSkillManagerAssessmentDetailInfoModel());
const assessmentSummary = ref(new AssessmentSummaryModel());
const trackSummary = ref<TrackSummaryModel[]>([]);
const skillSummary = ref<SkillSummaryModel[]>([]);
const candidateSummary = ref(new ProSkillManagerAssessmentCandidateSummaryModel());
const candidateIntegrityPageData = ref<PageModel<ProSkillManagerCandidateModel>>(new PageModel());

const pageNumber = ref(1);

const mergeCandidateList = (
    existing: PageModel<ProSkillManagerCandidateModel> | null | undefined,
    incoming: PageModel<ProSkillManagerCandidateModel>,
    page: number
): PageModel<ProSkillManagerCandidateModel> => {
  if (!existing || page <= 1) return incoming;

  const merged = new PageModel<ProSkillManagerCandidateModel>();
  merged.pageNumber = incoming.pageNumber;
  merged.last = incoming.last;
  merged.totalElementCount = incoming.totalElementCount;

  const prev = Array.isArray(existing.data) ? existing.data : [];
  const next = Array.isArray(incoming.data) ? incoming.data : [];
  merged.data = [...prev, ...next];

  return merged;
};

// ASMT-MANAGE-10: 평가 상세(ASSESSMENT 요약 + CANDIDATE 완료·무결성 후보자) 로드, 401/404/500 분기
const initData = async () => {
  loadingStore.openLoading("");
  try {
    const [
      detailInfo, summary, tracks, skills, candidateSum, candidateList
    ] = await Promise.all([
      proSkillManagerService.getProSkillManagerAssessmentDetailInfo(assessmentId.value),
      reportService.getReportAssessmentSummary(ReportDomainType.CUSTOM_ASSESSMENT, assessmentId.value),
      reportService.getReportTrackSummary(ReportDomainType.CUSTOM_ASSESSMENT, assessmentId.value),
      reportService.getReportSkillSummary(lang.value, ReportDomainType.CUSTOM_ASSESSMENT, assessmentId.value),
      proSkillManagerService.getProSkillManagerCandidateSummary(lang.value, assessmentId.value),
      proSkillManagerService.getProSkillManagerCandidate(
        lang.value,
        pageNumber.value,
        5,
        "latest,desc",
        assessmentId.value,
        "integrity=true",
        [ProSkillManagerCandidateStatusType.COMPLETED]
      )
    ]);

    assessmentDetailInfo.value = detailInfo;
    assessmentSummary.value = summary;
    trackSummary.value = tracks;
    skillSummary.value = skills;
    candidateSummary.value = candidateSum;
    candidateIntegrityPageData.value = candidateList;

  } catch (error: any) {
    if (error.response?.status === 401) {
      openDoNotHavePermissionPopup();
    }
    else if (error.response?.status === 404) {
      await router.push("/404")
      return;
    }
    else if (error.response?.status >= 500) {
      await router.push("/500")
      return;
    }
  } finally {
    isLoading.value = false;
    loadingStore.closeLoading();
  }
};

// ASMT-REPORT-05: 무결성 우려 응시자(integrity=true) 5명씩 Load More
const getIntegrityCandidateList = async () => {
  pageNumber.value++;
  const candidateList = await proSkillManagerService.getProSkillManagerCandidate(
    lang.value, pageNumber.value, 5, "latest,desc", assessmentId.value, "integrity=true", [ProSkillManagerCandidateStatusType.COMPLETED]
  );
  candidateIntegrityPageData.value = mergeCandidateList(
    candidateIntegrityPageData.value,
    candidateList,
    pageNumber.value
  );
};

provide(PRO_SKILL_MONITOR_CANDIDATE_REFRESH, getIntegrityCandidateList);

const getSortingReportSkillSummary = async (
    sortType: string,
    sortOrder: string,
    isSearchOrder: boolean
) => {
  skillSummary.value =
      await reportService.getReportSkillSummary(
          lang.value,
          ReportDomainType.CUSTOM_ASSESSMENT,
          assessmentId.value,
          isSearchOrder ? `${sortType},${sortOrder}` : undefined);
};

provide(PRO_SKILL_MONITOR_SORT_REFRESH, getSortingReportSkillSummary);

const openDoNotHavePermissionPopup = () => {
  openGenericAlert(
    { title: t("skillMonitor-main-limit-permission-title"), contents: t("skillMonitor-main-limit-permission-description"), popupClass: 'dark' },
    {
      confirm: { title: t("common-ok"), event: () => router.push({ name: "dashboard-proSkillManager" }) },
      close: { event: () => router.push({ name: "dashboard-proSkillManager" }) }
    }
  );
};

const handlerAssessmentMenuDoNotHavePermission = () => {
  if (!guard(
      userStore.isPermissionManageAssessment ||
      userStore.isSuperAdminType
  )) return;
}

onMounted(() => {
  if (assessmentId.value === 0) {
    dialogStore.open({ type: DialogType.ProSkillAssureSamplePopup, props: { isLightMode: true } });
  }
  handlerAssessmentMenuDoNotHavePermission();
  initData();
});

watch(() => route.query, () => {
  if (assessmentId.value === 0) {
    dialogStore.open({ type: DialogType.ProSkillAssureSamplePopup, props: { isLightMode: true } });
  }
  if (route.query.tab) {
    detailTab.value = route.query.tab as ReportTabType;
  }
}, { immediate: true });
</script>


<style>
@import "~/assets/scss/business_style.scss";
</style>